/**
 * The ghillie wrap (ROUND7 §3): scrim over the helmet, the shoulders and the back, in the colours of the
 * ground he lies on. The active palette is core + the mission's biome and the biome files share their ramp
 * names (05 §4.1) - which is how `uniformUS` dresses him for each theatre - so `ground`, `groundAlt` and
 * `foliage` are snow and conifer in the arctic, sand and scrub in the desert, grass and leaf in temperate
 * country, and this one builder wraps him for all three.
 *
 * It has to read at 640x360 with a 1 px outline, where he is about 30 px tall, and fine fringe would break
 * into loose dots there. So it is a few chunky pieces: a square of scrim turned across the helmet so its
 * corners stick out past the rim, the pack wrapped over, and six ragged tatters - three-sided shards, six
 * triangles each - hanging off the helmet, the shoulders and the pack, breaking the outline that gives a
 * man away. It REPLACES his webbing straps and the helmet's rim (both under it anyway): that is what keeps
 * him inside the 350-triangle character budget. The amber scarf stays clear of it (05 §4.3).
 */

import type { Color } from 'three';
import { type BoxColors, type MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { B } from './rig';

/** Runs `fn` with the builder bound to a bone and placed at its rest position (model.ts `at`). */
export type AtBone = (bone: number, fn: () => void) => void;

/** The wrap's colours, all from biome ramps. */
export interface GhillieColors {
  /** Helmet crown and the tops of the scrim, the pack and the shoulders: what the camera looks down on. */
  top: Color;
  /** The scrim's sides and the pack's. */
  side: Color;
  /** The tatters, dark to light: they alternate so neighbouring shards never share a tone. */
  tatters: readonly [Color, Color, Color];
}

export function ghillieColors(p: Palette): GhillieColors {
  return {
    top: p.color('ground.1'),
    side: p.color('groundAlt.1'),
    tatters: [p.color('ground.0'), p.color('foliage.1'), p.color('groundAlt.1')],
  };
}

/** The pack under the scrim: the same box, in the wrap's colours. */
export function wrappedPack(c: GhillieColors): BoxColors {
  return { all: c.side, top: c.top };
}

/**
 * A three-sided shard `len` long from (x, y, z) in the bone's frame, built tip-up and then tipped by `pitch`
 * and `roll`. It is spun about its own axis first, so no face of it lines up with the body's boxes.
 */
function shard(
  mb: MeshBuilder, color: Color, x: number, y: number, z: number, len: number, pitch: number, roll: number,
): void {
  mb.with(place(x, y, z, 0, 1, pitch, roll), () => mb.cylinder(0.1, 0, len, 3, color, place(0, 0, 0, 0.4)));
}

/** A tatter hanging from (x, y, z), its tip swung out by `outX` / `outZ` radians (+x his left, +z his front). */
function tatter(
  mb: MeshBuilder, color: Color, x: number, y: number, z: number, len: number, outX: number, outZ: number,
): void {
  shard(mb, color, x, y, z, len, Math.PI - outZ, -outX);
}

/** Everything the wrap adds to the rig: the helmet scrim and tatters on the head, the rest on the spine. */
export function ghillieWrap(mb: MeshBuilder, p: Palette, at: AtBone): void {
  const c = ghillieColors(p);
  const [dark, leaf, light] = c.tatters;
  at(B.head, () => {
    // A square of scrim over the crown, turned 24 degrees so its corners stand out past the helmet's
    // rim: from above the head is a ragged star, not a neat square. Tipped back, so that with his head
    // down in the crouch it lies flat instead of standing up over cover behind him.
    mb.box(0.48, 0.07, 0.48, { all: c.side, top: c.top }, place(0, 0.39, 0.02, 0.42, 1, -0.3));
    tatter(mb, light, 0.06, 0.34, -0.2, 0.34, 0.1, -0.35);
  });
  at(B.spine, () => {
    // Over the shoulders, hanging outside the upper arms.
    tatter(mb, dark, 0.3, 0.5, -0.02, 0.36, 0.6, -0.1);
    tatter(mb, leaf, -0.3, 0.5, 0.0, 0.32, -0.55, 0.05);
    // Off the bottom of the pack, down over his seat, and one tuft standing up off its top.
    tatter(mb, leaf, 0.12, 0.1, -0.3, 0.44, 0.12, -0.3);
    tatter(mb, dark, -0.1, 0.14, -0.33, 0.34, -0.15, -0.4);
    shard(mb, light, 0.04, 0.48, -0.28, 0.3, -0.55, -0.2);
  });
}
